import { useSearchParams } from 'react-router-dom';

type BaseListSearchParams = {
  pageIndex: number;
  pageSize: number;
  search: string;
};

type UseListPageSearchParamsOptions<T extends BaseListSearchParams> = {
  detailKey?: string;
  path: string;
  defaults?: Partial<BaseListSearchParams>;
  createParams: (params: BaseListSearchParams, searchParams: URLSearchParams) => T;
};

const DEFAULT_PAGE_INDEX = 1;
const DEFAULT_PAGE_SIZE = 10;

const getPositiveNumber = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const useListPageSearchParams = <T extends BaseListSearchParams>({
  detailKey = 'companyId',
  path,
  defaults,
  createParams,
}: UseListPageSearchParamsOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const detailId = searchParams.get(detailKey);

  const listParams = createParams(
    {
      pageIndex: getPositiveNumber(searchParams.get('pageIndex'), defaults?.pageIndex ?? DEFAULT_PAGE_INDEX),
      pageSize: getPositiveNumber(searchParams.get('pageSize'), defaults?.pageSize ?? DEFAULT_PAGE_SIZE),
      search: searchParams.get('search') || defaults?.search || '',
    },
    searchParams
  );

  const updateListParams = (nextParams: Partial<T>) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const pageIndex = nextParams.pageIndex ?? listParams.pageIndex;
    const pageSize = nextParams.pageSize ?? listParams.pageSize;
    const search = nextParams.search ?? listParams.search;

    nextSearchParams.set('pageIndex', String(pageIndex));
    nextSearchParams.set('pageSize', String(pageSize));
    if (search) {
      nextSearchParams.set('search', search);
    } else {
      nextSearchParams.delete('search');
    }

    Object.entries(nextParams).forEach(([key, value]) => {
      if (key === 'pageIndex' || key === 'pageSize' || key === 'search') {
        return;
      }
      if (value === undefined) {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length > 0) {
          nextSearchParams.set(key, value.join(','));
        } else {
          nextSearchParams.delete(key);
        }
        return;
      }
      if (value === null || value === '') {
        nextSearchParams.delete(key);
      } else {
        nextSearchParams.set(key, String(value));
      }
    });

    setSearchParams(nextSearchParams);
  };

  const buildDetailUrl = (nextDetailId: string | number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(detailKey, String(nextDetailId));
    return `${path}?${nextSearchParams.toString()}`;
  };

  const clearDetailParam = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(detailKey);
    setSearchParams(nextSearchParams);
  };

  return {
    detailId,
    listParams,
    updateListParams,
    buildDetailUrl,
    clearDetailParam,
  };
};

export default useListPageSearchParams;
